/**
 * Image Caching Utility for Offline Support
 * Downloads and caches images from WIP details for offline access
 */

const IMAGE_CACHE_NAME = 'pragyaa-images-v1';
const CACHED_IMAGES_KEY = 'frappe_cached_images';

export class ImageCacheManager {
  constructor() {
    this.cachePromise = this.initCache();
  }

  async initCache() {
    if ('caches' in window) {
      return caches.open(IMAGE_CACHE_NAME);
    }
    return null;
  }

  /**
   * Extract all image URLs from WIP details response
   */
  extractImageUrls(wipData) {
    const imageUrls = new Set();

    // Extract from styles_wip data
    if (wipData.styles_wip && Array.isArray(wipData.styles_wip)) {
      wipData.styles_wip.forEach(item => {
        // Add various image fields
        if (item.image) imageUrls.add(item.image);
        if (item.brand_logo) imageUrls.add(item.brand_logo);
        if (item.customer_logo) imageUrls.add(item.customer_logo);
      });
    }

    // Extract from item_and_defect_properties
    if (wipData.item_and_defect_properties && Array.isArray(wipData.item_and_defect_properties)) {
      wipData.item_and_defect_properties.forEach(item => {
        if (item.image) imageUrls.add(item.image);
        if (item.brand_logo) imageUrls.add(item.brand_logo);
        if (item.customer_logo) imageUrls.add(item.customer_logo);
      });
    }

    // Convert relative URLs to absolute URLs
    const baseUrl = process.env.REACT_APP_FRAPPE_URL || 'http://pra.localhost:8000';
    return Array.from(imageUrls).map(url => {
      if (url.startsWith('/')) {
        return `${baseUrl}${url}`;
      }
      return url;
    }).filter(url => this.isValidImageUrl(url));
  }

  /**
   * Check if URL is a valid image URL
   */
  isValidImageUrl(url) {
    try {
      const urlObj = new URL(url);
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'];
      const pathname = urlObj.pathname.toLowerCase();
      
      // Check if it has a valid image extension or is from files endpoint
      return validExtensions.some(ext => pathname.endsWith(ext)) || 
             pathname.includes('/files/') ||
             pathname.includes('/assets/');
    } catch (error) {
      console.warn('Invalid URL for image caching:', url);
      return false;
    }
  }

  /**
   * Process a single image in a batch
   */
  async processBatchImage(url, totalUrls, onProgress, counters) {
    const success = await this.cacheImage(url);
    
    if (onProgress) {
      onProgress({
        total: totalUrls.length,
        cached: success ? counters.cached + 1 : counters.cached,
        failed: success ? counters.failed : counters.failed + 1,
        current: url
      });
    }
    
    return { success, url };
  }

  /**
   * Cache a single image
   */
  async cacheImage(imageUrl) {
    try {
      const cache = await this.cachePromise;
      if (!cache) {
        console.warn('Cache API not available');
        return false;
      }

      // Check if image is already cached
      const cached = await cache.match(imageUrl);
      if (cached) {
        console.log('Image already cached:', imageUrl);
        return true;
      }

      // Fetch and cache the image
      console.log('Caching image:', imageUrl);
      const response = await fetch(imageUrl, {
        credentials: 'include',
        mode: 'cors'
      });

      if (response.ok) {
        // Clone the response before putting in cache
        await cache.put(imageUrl, response.clone());
        console.log('Successfully cached image:', imageUrl);
        return true;
      } else {
        console.warn('Failed to fetch image for caching:', imageUrl, response.status);
        return false;
      }
    } catch (error) {
      console.error('Error caching image:', imageUrl, error);
      return false;
    }
  }

  /**
   * Cache multiple images with progress tracking
   */
  async cacheImages(imageUrls, onProgress) {
    if (!imageUrls.length) {
      console.log('No images to cache');
      return { success: true, cached: 0, failed: 0 };
    }

    console.log(`Starting to cache ${imageUrls.length} images`);
    
    let cached = 0;
    let failed = 0;

    // Process images with a reasonable concurrency limit
    const batchSize = 3;
    const batches = [];
    
    for (let i = 0; i < imageUrls.length; i += batchSize) {
      batches.push(imageUrls.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const batchPromises = batch.map((url) => {
        return this.processBatchImage(url, imageUrls, onProgress, { cached, failed });
      });

      const results = await Promise.all(batchPromises);
      
      // Update counters based on results
      results.forEach(result => {
        if (result.success) {
          cached++;
        } else {
          failed++;
        }
      });
    }

    // Store list of cached images in localStorage for reference
    this.updateCachedImagesList(imageUrls.filter((_, index) => index < cached));

    console.log(`Image caching complete: ${cached} cached, ${failed} failed`);
    
    return {
      success: failed === 0,
      total: imageUrls.length,
      cached,
      failed
    };
  }

  /**
   * Store list of cached images for offline reference
   */
  updateCachedImagesList(imageUrls) {
    try {
      const existingImages = this.getCachedImagesList();
      const combinedImages = [...new Set([...existingImages, ...imageUrls])];
      localStorage.setItem(CACHED_IMAGES_KEY, JSON.stringify(combinedImages));
      console.log(`Updated cached images list: ${combinedImages.length} total images`);
    } catch (error) {
      console.error('Failed to update cached images list:', error);
    }
  }

  /**
   * Get list of cached images from localStorage
   */
  getCachedImagesList() {
    try {
      const cached = localStorage.getItem(CACHED_IMAGES_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.error('Failed to get cached images list:', error);
      return [];
    }
  }

  /**
   * Check if a specific image is cached
   */
  async isImageCached(imageUrl) {
    try {
      const cache = await this.cachePromise;
      if (!cache) return false;
      
      const cached = await cache.match(imageUrl);
      return !!cached;
    } catch (error) {
      console.error('Error checking if image is cached:', error);
      return false;
    }
  }

  /**
   * Get cached image as blob URL for offline use
   */
  async getCachedImageBlob(imageUrl) {
    try {
      const cache = await this.cachePromise;
      if (!cache) return null;
      
      const cached = await cache.match(imageUrl);
      if (cached) {
        const blob = await cached.blob();
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error('Error getting cached image blob:', error);
      return null;
    }
  }

  /**
   * Clear all cached images
   */
  async clearImageCache() {
    try {
      const cache = await this.cachePromise;
      if (cache) {
        const keys = await cache.keys();
        await Promise.all(keys.map(key => cache.delete(key)));
        console.log('Cleared image cache');
      }
      localStorage.removeItem(CACHED_IMAGES_KEY);
    } catch (error) {
      console.error('Error clearing image cache:', error);
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    try {
      const cache = await this.cachePromise;
      if (!cache) return { supported: false };
      
      const keys = await cache.keys();
      const cachedImages = this.getCachedImagesList();
      
      return {
        supported: true,
        cachedCount: keys.length,
        listedCount: cachedImages.length,
        cacheSize: await this.calculateCacheSize(keys)
      };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return { supported: false, error: error.message };
    }
  }

  /**
   * Calculate approximate cache size
   */
  async calculateCacheSize(keys) {
    try {
      const cache = await this.cachePromise;
      if (!cache) return 0;
      
      let totalSize = 0;
      
      // Sample a few images to estimate total size (checking all would be slow)
      const sampleSize = Math.min(5, keys.length);
      const sampleKeys = keys.slice(0, sampleSize);
      
      for (const key of sampleKeys) {
        const response = await cache.match(key);
        if (response) {
          const blob = await response.blob();
          totalSize += blob.size;
        }
      }
      
      // Estimate total size based on sample
      return keys.length > 0 ? Math.round((totalSize / sampleSize) * keys.length) : 0;
    } catch (error) {
      console.error('Error calculating cache size:', error);
      return 0;
    }
  }
}

// Create a singleton instance
export const imageCacheManager = new ImageCacheManager();